import {
  useQuery,
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseQueryResult
} from 'react-query'
import {
  fetchBankAccounts,
  createBankAccount,
  activateBankAccount,
  archiveBankAccount,
  fetchAmlQuestions,
  submitAmlAnswers,
  fetchIdentityVerificationStatus,
  requestIdentityVerification,
  startDiditVerification,
  fetchCustomerPopup,
  recordPopupInteraction,
  BankAccount,
  CreateBankAccountRequest,
  AmlQuestionsResponse,
  AmlAnswerIn,
  IdentityVerificationStatus,
  IdentityVerificationRequest,
  DiditSessionResponse,
  CustomerPopup
} from 'api/compliance'

export function useBankAccounts(): UseQueryResult<BankAccount[]> {
  return useQuery(['bank-accounts'], fetchBankAccounts, { staleTime: 60_000 })
}

export function useMutateCreateBankAccount(): UseMutationResult<
  BankAccount,
  unknown,
  CreateBankAccountRequest
> {
  const qc = useQueryClient()
  return useMutation(createBankAccount, {
    onSuccess: () => {
      qc.invalidateQueries(['bank-accounts'])
    }
  })
}

export function useMutateActivateBankAccount(): UseMutationResult<
  BankAccount,
  unknown,
  number
> {
  const qc = useQueryClient()
  return useMutation(activateBankAccount, {
    onSuccess: () => {
      qc.invalidateQueries(['bank-accounts'])
    }
  })
}

export function useMutateArchiveBankAccount(): UseMutationResult<
  void,
  unknown,
  number
> {
  const qc = useQueryClient()
  return useMutation(archiveBankAccount, {
    onSuccess: () => {
      qc.invalidateQueries(['bank-accounts'])
    }
  })
}

export function useAmlQuestions(): UseQueryResult<AmlQuestionsResponse> {
  return useQuery(['aml'], fetchAmlQuestions, { staleTime: 60_000 })
}

export function useMutateAmlAnswers(): UseMutationResult<
  void,
  unknown,
  AmlAnswerIn[]
> {
  return useMutation(submitAmlAnswers)
}

export function useIdentityVerification(): UseQueryResult<IdentityVerificationStatus> {
  return useQuery(['identity-verification'], fetchIdentityVerificationStatus, {
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always'
  })
}

export function useMutateRequestIdentityVerification(): UseMutationResult<
  IdentityVerificationStatus,
  unknown,
  IdentityVerificationRequest
> {
  const qc = useQueryClient()
  return useMutation(requestIdentityVerification, {
    onSuccess: () => {
      qc.invalidateQueries(['identity-verification'])
    }
  })
}

// Didit hosted IDV — the live KYC (replaces useMutateRequestIdentityVerification/Cloudcheck).
// Takes the return URL (callback) and resolves to the hosted-session URL to redirect to.
export function useMutateStartDiditVerification(): UseMutationResult<
  DiditSessionResponse,
  unknown,
  string | undefined
> {
  const qc = useQueryClient()
  return useMutation(startDiditVerification, {
    onSuccess: () => {
      qc.invalidateQueries(['identity-verification'])
    }
  })
}

export function useCustomerPopup(): UseQueryResult<CustomerPopup | null> {
  return useQuery(['popups'], fetchCustomerPopup, { staleTime: 300_000 })
}

export function useMutatePopupInteraction(): UseMutationResult<
  void,
  unknown,
  number
> {
  const qc = useQueryClient()
  return useMutation(recordPopupInteraction, {
    onSuccess: () => {
      qc.invalidateQueries(['popups'])
    }
  })
}
